import { useEffect, useRef, useState } from 'react';
import { ArrowClockwise, CloudArrowUp, DownloadSimple, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { resolveAssetUrl } from '../lib/utils';
import ContextHelpButton from './ContextHelpButton';
import MasterPdfStyleSelector, { defaultPdfStyle } from './MasterPdfStyleSelector';

const SIGNATURE_INTERNAL_PHOTOS = 3;
const PDF_UPLOAD_MAX_EDGE = 1600;
const PDF_UPLOAD_DIRECT_BYTES = 1_500_000;
const PDF_UPLOAD_JPEG_QUALITY = 0.90;
const MOBILE_PANELS = [
  ['templates', 'Template'],
  ['preview', 'Anteprima'],
  ['photos', 'Foto'],
];

function filenameFromDisposition(value, fallback) {
  const match = String(value || '').match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

function pdfFilename(content) {
  const master = content?.content_master || {};
  const source = String(content?.hook_text || master.primary_hook || content?.title || master.title || 'content-master')
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100);
  return `${source || 'content-master'}.pdf`;
}

export async function preparePdfUploadFile(file) {
  if (!file || typeof window === 'undefined' || typeof document === 'undefined') return file;
  const createBitmap = window.createImageBitmap || globalThis.createImageBitmap;
  if (typeof createBitmap !== 'function') return file;

  let bitmap;
  try {
    bitmap = await createBitmap(file);
    const width = Number(bitmap?.width || 0);
    const height = Number(bitmap?.height || 0);
    const longest = Math.max(width, height);
    if (!longest) return file;
    if (longest <= PDF_UPLOAD_MAX_EDGE && Number(file.size || 0) <= PDF_UPLOAD_DIRECT_BYTES) return file;

    const scale = Math.min(1, PDF_UPLOAD_MAX_EDGE / longest);
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context || typeof canvas.toBlob !== 'function') return file;
    context.fillStyle = '#fff';
    context.fillRect(0, 0, targetWidth, targetHeight);
    context.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', PDF_UPLOAD_JPEG_QUALITY));
    if (!blob) return file;
    const base = String(file.name || 'foto').replace(/\.[^.]+$/, '') || 'foto';
    return new File([blob], `${base}.jpg`, {
      type: 'image/jpeg',
      lastModified: Number(file.lastModified || Date.now()),
    });
  } catch {
    return file;
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

function mobilePreviewDocument(document) {
  const support = `
<style data-sketchario-mobile-pdf-preview="true">
html,body{width:100%;min-height:100%;margin:0!important;padding:0!important;background:#191922!important;overflow-x:hidden!important}
body{overflow-y:auto!important}
main{display:block!important;width:100%!important;margin:0!important;padding:12px 0 24px!important}
.sketchario-mobile-page-shell{position:relative!important;margin:0 auto 16px!important;overflow:visible!important;flex:none!important}
.page{margin:0!important;transform-origin:top left!important;box-shadow:0 14px 36px rgba(0,0,0,.28)!important}
</style>
<script>
(function(){
  function fitPages(){
    var pages=Array.prototype.slice.call(document.querySelectorAll('.page'));
    var viewportWidth=Math.max(240,document.documentElement.clientWidth-24);
    var viewportHeight=Math.max(360,document.documentElement.clientHeight-24);
    pages.forEach(function(page){
      var shell=page.parentElement;
      if(!shell||!shell.classList.contains('sketchario-mobile-page-shell')){
        shell=document.createElement('div');
        shell.className='sketchario-mobile-page-shell';
        page.parentNode.insertBefore(shell,page);
        shell.appendChild(page);
      }
      page.style.transform='none';
      var naturalWidth=page.offsetWidth;
      var naturalHeight=page.offsetHeight;
      if(!naturalWidth||!naturalHeight)return;
      var scale=Math.min(1,viewportWidth/naturalWidth,viewportHeight/naturalHeight);
      page.style.transform='scale('+scale+')';
      shell.style.width=Math.ceil(naturalWidth*scale)+'px';
      shell.style.height=Math.ceil(naturalHeight*scale)+'px';
    });
  }
  window.addEventListener('DOMContentLoaded',fitPages);
  window.addEventListener('load',fitPages);
  window.addEventListener('resize',fitPages);
  setTimeout(fitPages,0);
})();
</script>`;
  const source = String(document || '');
  return source.includes('</head>') ? source.replace('</head>', `${support}</head>`) : `${support}${source}`;
}

function ImageRole({ title, value, items, onChange, onDelete, deletingId, optional = true, helpArticleId = 'pdf-images' }) {
  return <div>
    <div className="mb-2 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[.12em] text-white">{title}{optional && <span className="ml-1 font-normal normal-case tracking-normal text-white/50">opzionale</span>}</p>
        <ContextHelpButton articleId={helpArticleId} label={`Guida: ${title}`} />
      </div>
      {value && <button type="button" className="min-h-9 px-2 text-xs text-fuchsia-300 hover:text-white lg:min-h-0 lg:px-0 lg:text-[10px]" onClick={() => onChange('')}>Nessuna</button>}
    </div>
    <div className="flex gap-3 overflow-x-auto pb-2 lg:gap-2">
      {items.map((item, index) => {
        const id = String(item.id || '');
        const selected = id === value;
        const name = item.original_name || item.filename || `Immagine ${index + 1}`;
        return <div key={id || item.url || index} className="group relative h-[94px] w-[78px] shrink-0 lg:h-[78px] lg:w-[62px]">
          <button
            type="button"
            className={`h-[88px] w-[72px] overflow-hidden rounded-xl border-2 lg:h-[74px] lg:w-[58px] lg:rounded-lg ${selected ? 'border-fuchsia-400' : 'border-white/15 hover:border-white/50'}`}
            aria-pressed={selected}
            title={name}
            onClick={() => onChange(selected ? '' : id)}
          ><img src={resolveAssetUrl(item.url)} alt={name} className="h-full w-full object-cover" /></button>
          {id && <button
            type="button"
            className="absolute -right-1 -top-1 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/30 bg-black/90 text-white opacity-100 transition-opacity hover:bg-rose-600 lg:h-5 lg:w-5 lg:opacity-0 lg:group-hover:opacity-100 lg:focus:opacity-100"
            onClick={() => onDelete(item)}
            disabled={Boolean(deletingId)}
            aria-label={`Elimina ${name}`}
          >{deletingId === id ? <span className="text-[8px]">…</span> : <X size={12} weight="bold" />}</button>}
        </div>;
      })}
      {!items.length && <div className="flex h-[88px] min-w-[180px] items-center justify-center rounded-xl border border-dashed border-white/15 px-4 text-xs text-white/45 lg:h-[74px] lg:min-w-[150px] lg:rounded-lg lg:text-[10px]">Nessuna foto caricata</div>}
    </div>
  </div>;
}

export default function MasterPdfStudio({ content, api, isAdmin = false, onClose }) {
  const inputRef = useRef(null);
  const previewUrlRef = useRef('');
  const [template, setTemplate] = useState(() => defaultPdfStyle(isAdmin));
  const [visualMode, setVisualMode] = useState('graphics');
  const [coverId, setCoverId] = useState('');
  const [bookId, setBookId] = useState('');
  const [readability, setReadability] = useState('template');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewStale, setPreviewStale] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [mobilePanel, setMobilePanel] = useState('preview');
  const signatureSelected = isAdmin && template === 'private_signature';

  const params = () => ({
    visual_mode: signatureSelected ? visualMode : 'client_media',
    pdf_template: template,
    cover_image_id: coverId,
    book_cover_image_id: bookId,
    cover_readability: readability,
  });

  const replacePreviewUrl = (next) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = next || '';
    setPreviewUrl(next || '');
  };

  const loadImages = async () => {
    if (!content.project_id) return;
    try {
      const response = await api.get(`/media/library/${content.project_id}`);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setImages(rows.filter(item => item?.url && item.source === 'master_pdf_upload' && String(item.context_id || '') === String(content.id) && (item.type === 'image' || String(item.mime_type || '').startsWith('image/'))));
    } catch {}
  };

  const refreshPreview = async () => {
    if (previewing) return;
    setPreviewing(true);
    setPreviewError('');
    try {
      const mobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches;
      if (mobile) {
        const response = await api.get(`/pro/master-projects/contents/${content.id}/preview/html`, {
          responseType: 'text',
          params: params(),
        });
        replacePreviewUrl('');
        setPreviewHtml(mobilePreviewDocument(response.data));
      } else {
        const response = await api.get(`/pro/master-projects/contents/${content.id}/preview/pdf`, {
          responseType: 'blob',
          params: params(),
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        setPreviewHtml('');
        replacePreviewUrl(URL.createObjectURL(blob));
      }
      setPreviewStale(false);
    } catch (error) {
      const detail = error.response?.data?.detail?.message || error.response?.data?.detail || error.message;
      setPreviewError(`Anteprima non disponibile: ${detail || 'errore di impaginazione'}`);
    } finally {
      setPreviewing(false);
    }
  };

  const openPdfPreview = async () => {
    const popup = typeof window !== 'undefined' ? window.open('', '_blank') : null;
    try {
      const response = await api.get(`/pro/master-projects/contents/${content.id}/preview/pdf`, { responseType: 'blob', params: params() });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (popup) popup.location.replace(url);
      else if (typeof window !== 'undefined') window.open(url, '_blank');
      window.setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (error) {
      if (popup) popup.close();
      const detail = error.response?.data?.detail?.message || error.response?.data?.detail || error.message;
      toast.error(`Anteprima PDF: ${detail || 'impossibile aprire il PDF'}`);
    }
  };

  useEffect(() => {
    loadImages();
    refreshPreview();
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, [content.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutate = (setter, value) => {
    setter(value);
    setPreviewStale(true);
  };

  const selectTemplate = (value) => {
    mutate(setTemplate, value);
    if (value !== 'private_signature') setMobilePanel('photos');
  };

  const uploadImages = async (fileList) => {
    const files = Array.from(fileList || []).filter(file => file.type.startsWith('image/'));
    if (!files.length || uploading || !content.project_id) return;
    setUploading(true);
    const uploaded = [];
    let failed = 0;
    let lastError = null;
    try {
      for (const original of files) {
        try {
          const file = await preparePdfUploadFile(original);
          const body = new FormData();
          body.append('files', file);
          body.append('purpose', 'master_pdf');
          body.append('context_id', content.id);
          const { data } = await api.post(`/media/library/${content.project_id}`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
          const created = Array.isArray(data?.items) ? data.items.filter(item => item?.url) : [];
          if (created.length) uploaded.push(...created);
          else failed += 1;
        } catch (error) {
          failed += 1;
          lastError = error;
        }
      }

      if (uploaded.length) {
        setImages(previous => {
          const merged = [...uploaded, ...previous].filter(item => item?.url);
          return merged.filter((item, index) => merged.findIndex(candidate => String(candidate.id || candidate.url) === String(item.id || item.url)) === index);
        });
        if (signatureSelected) setVisualMode('client_media');
        setPreviewStale(true);
        setMobilePanel('photos');
        const partial = failed ? ` · ${failed} ${failed === 1 ? 'non caricata' : 'non caricate'}` : '';
        toast.success(`${uploaded.length} ${uploaded.length === 1 ? 'immagine caricata' : 'immagini caricate'}${partial}`);
      } else {
        toast.error(lastError?.response?.data?.detail || 'Impossibile caricare le immagini');
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const deleteImage = async (item) => {
    const id = String(item?.id || '');
    if (!id || deletingId || !content.project_id) return;
    setDeletingId(id);
    try {
      await api.delete(`/media/library/${content.project_id}/${id}`);
      setImages(previous => previous.filter(candidate => String(candidate.id || '') !== id));
      if (coverId === id) { setCoverId(''); setReadability('template'); }
      if (bookId === id) setBookId('');
      setPreviewStale(true);
      toast.success('Immagine rimossa');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile eliminare l’immagine');
    } finally { setDeletingId(''); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/pro/master-projects/contents/${content.id}/export/pdf`, { responseType: 'blob', params: params() });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filenameFromDisposition(response.headers?.['content-disposition'], pdfFilename(content));
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success('Lead magnet PDF esportato');
    } catch (error) {
      const detail = error.response?.data?.detail?.message || error.response?.data?.detail || error.message;
      toast.error(`Export PDF: ${detail || 'impossibile creare il PDF'}`);
    } finally { setExporting(false); }
  };

  const reserved = new Set([coverId, bookId].filter(Boolean));
  const signatureRequired = SIGNATURE_INTERNAL_PHOTOS + reserved.size;
  const signatureMissing = Math.max(0, signatureRequired - images.length);
  const hasPreview = Boolean(previewUrl || previewHtml);

  return <div className="fixed inset-0 z-[90] flex h-[100dvh] flex-col overflow-hidden bg-[#0d0d12] text-white" role="dialog" aria-modal="true" aria-label="PDF Studio" data-testid="master-pdf-studio" onClick={event => event.stopPropagation()}>
    <header className="flex min-h-16 shrink-0 items-center justify-between gap-2 border-b border-white/10 px-3 py-2 sm:px-5" style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}>
      <div className="min-w-0 flex flex-1 items-center gap-2">
        <div className="min-w-0"><p className="text-[9px] font-semibold uppercase tracking-[.18em] text-fuchsia-300 sm:text-[10px]">PDF Studio</p><h2 className="truncate text-xs font-bold sm:text-sm">{content.title || content.content_master?.title || 'Content Master'}</h2></div>
        <ContextHelpButton articleId="pdf-master-pack" label="Guida PDF Studio" />
      </div>
      <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
        <button type="button" className="btn-ghost hidden items-center gap-2 px-3 py-2 text-xs lg:inline-flex" onClick={refreshPreview} disabled={previewing} data-testid="master-pdf-studio-refresh"><ArrowClockwise size={17} className={previewing ? 'animate-spin' : ''} />{previewing ? 'Rendering…' : 'Aggiorna anteprima'}</button>
        <button type="button" className="btn-gradient inline-flex min-h-10 items-center gap-2 px-2.5 py-2 text-xs sm:px-3" onClick={exportPdf} disabled={exporting || previewing} data-testid="master-pdf-studio-export" aria-label={exporting ? 'Esportazione PDF in corso' : 'Esporta PDF'}><DownloadSimple size={18} /><span className="hidden sm:inline">{exporting ? 'Esporto…' : 'Esporta PDF'}</span></button>
        <button type="button" className="btn-ghost inline-flex h-10 w-10 items-center justify-center p-0" onClick={onClose} aria-label="Chiudi PDF Studio" data-testid="master-pdf-studio-close"><X size={20} /></button>
      </div>
    </header>

    <div className="relative min-h-0 flex-1 overflow-hidden lg:grid lg:grid-cols-[310px_minmax(0,1fr)_330px]">
      <aside className={`${mobilePanel === 'templates' ? 'block' : 'hidden'} absolute inset-0 overflow-y-auto bg-[#121219] p-4 pb-28 lg:static lg:block lg:border-r lg:border-white/10 lg:pb-4`} data-testid="master-pdf-studio-templates" data-mobile-active={mobilePanel === 'templates'}>
        <div className="mb-3 flex items-center justify-between gap-2"><p className="text-xs font-semibold">Template PDF</p><ContextHelpButton articleId="pdf-master-pack" label="Guida template PDF" /></div>
        <MasterPdfStyleSelector value={template} onChange={selectTemplate} isAdmin={isAdmin} disabled={previewing || exporting} variant="studio" />
        <button type="button" className="btn-gradient mt-4 flex w-full items-center justify-center gap-2 py-3 text-xs lg:hidden" onClick={() => setMobilePanel('preview')}>Vedi anteprima</button>
      </aside>

      <main className={`${mobilePanel === 'preview' ? 'flex' : 'hidden'} absolute inset-0 min-h-0 flex-col items-center justify-center overflow-hidden bg-[#191922] p-3 pb-24 lg:static lg:flex lg:p-6`} data-testid="master-pdf-studio-preview" data-mobile-active={mobilePanel === 'preview'}>
        <div className="mb-2 flex w-full max-w-[820px] shrink-0 items-center gap-2 lg:hidden" data-testid="master-pdf-studio-mobile-preview-actions">
          <button type="button" className="btn-ghost flex min-h-11 flex-1 items-center justify-center gap-2 px-3 py-2 text-xs" onClick={refreshPreview} disabled={previewing}><ArrowClockwise size={17} className={previewing ? 'animate-spin' : ''} />{previewing ? 'Rendering…' : (previewStale ? 'Aggiorna' : 'Rigenera')}</button>
          <button type="button" className="btn-gradient flex min-h-11 flex-1 items-center justify-center gap-2 px-3 py-2 text-xs" onClick={openPdfPreview} disabled={previewing} data-testid="master-pdf-studio-open-preview"><DownloadSimple size={17} />Apri PDF</button>
        </div>
        <div className="relative flex min-h-0 w-full max-w-[820px] flex-1 items-center justify-center">
          {previewStale && hasPreview && <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 whitespace-nowrap rounded-full border border-amber-400/30 bg-[#2a2418]/95 px-3 py-1 text-[10px] text-amber-100">Modifiche non applicate</div>}
          {previewing && <div className="flex flex-col items-center gap-3 text-white/60"><ArrowClockwise size={28} className={previewing ? 'animate-spin' : ''} /><span className="text-xs">Impaginazione e controllo overflow…</span></div>}
          {!previewing && previewError && <div className="max-w-md rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-100" data-testid="master-pdf-studio-preview-error">{previewError}<p className="mt-2 text-xs text-white/55">Il validator ha bloccato un PDF non sicuro invece di esportarlo con testi sovrapposti.</p></div>}
          {!previewing && !previewError && previewHtml && <iframe title="Anteprima PDF mobile" srcDoc={previewHtml} className="h-full min-h-[300px] w-full rounded-lg border-0 bg-transparent lg:hidden" data-testid="master-pdf-studio-mobile-html-preview" />}
          {!previewing && !previewError && previewUrl && <iframe title="Anteprima PDF" src={`${previewUrl}#toolbar=0&navpanes=0&view=Fit`} className="hidden h-full min-h-[300px] w-full rounded-lg border border-white/10 bg-white shadow-2xl lg:block lg:max-h-[calc(100vh-112px)]" data-testid="master-pdf-studio-iframe" />}
        </div>
      </main>

      <aside className={`${mobilePanel === 'photos' ? 'block' : 'hidden'} absolute inset-0 overflow-y-auto bg-[#121219] p-4 pb-28 lg:static lg:block lg:border-l lg:border-white/10 lg:pb-4`} data-testid="master-pdf-studio-controls" data-mobile-active={mobilePanel === 'photos'}>
        <div className="space-y-5">
          {signatureSelected ? <>
            <section>
              <div className="mb-2 flex items-center justify-between gap-2"><p className="text-sm font-semibold lg:text-xs">Supporto visivo</p><ContextHelpButton articleId="pdf-master-pack" label="Guida supporto visivo PDF" /></div>
              <div className="grid grid-cols-2 gap-2">
                {[['graphics','Solo grafica'],['client_media','Foto caricate']].map(([value,label]) => <button key={value} type="button" className="min-h-11 rounded-lg border px-2 py-3 text-xs font-semibold lg:min-h-0 lg:py-2.5 lg:text-[10px]" style={visualMode===value?{borderColor:'var(--accent-purple)',background:'rgba(168,85,247,.18)'}:{borderColor:'var(--border-color)'}} aria-pressed={visualMode===value} onClick={() => mutate(setVisualMode, value)}>{label}</button>)}
              </div>
            </section>
            <section className="rounded-xl border border-white/10 bg-white/[.03] p-3 text-xs leading-relaxed lg:rounded-lg lg:text-[10px]">
              <div className="mb-1 flex items-center justify-between gap-2"><p className="font-semibold">Foto interne: {SIGNATURE_INTERNAL_PHOTOS}</p><ContextHelpButton articleId="pdf-images" label="Guida immagini PDF" /></div>
              <p className={signatureMissing ? 'text-amber-200' : 'text-emerald-200'}>{signatureMissing ? `Caricate ${images.length}. Ne mancano ${signatureMissing} considerando le immagini riservate.` : `${images.length} caricate · quantità sufficiente.`}</p>
              <p className="mt-1 text-white/50">Il comportamento Signature resta invariato.</p>
            </section>
          </> : <section className="rounded-xl border border-white/10 bg-white/[.03] p-3 text-xs leading-relaxed lg:rounded-lg lg:text-[10px]">
            <p className="font-semibold text-white">PDF pubblico semplificato</p>
            <p className="mt-1 text-white/60">Una foto per l’apertura. Nessuna foto interna. Una seconda immagine è opzionale solo per la CTA.</p>
          </section>}

          <section>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={event => uploadImages(event.target.files)} data-testid="master-pdf-studio-upload-input" />
            <button type="button" className="btn-gradient flex min-h-12 w-full items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm lg:min-h-0 lg:rounded-lg lg:py-2.5 lg:text-xs" onClick={() => inputRef.current?.click()} disabled={uploading}><CloudArrowUp size={19} />{uploading ? 'Caricamento…' : 'Carica immagini per questo PDF'}</button>
          </section>

          {!signatureSelected && <>
            <ImageRole title="Foto apertura" optional={false} value={coverId} items={images} deletingId={deletingId} onDelete={deleteImage} onChange={value => { mutate(setCoverId, value); if (!value) setReadability('template'); }} />
            {coverId && <section>
              <div className="mb-2 flex items-center justify-between gap-2"><p className="text-[10px] font-semibold uppercase tracking-[.12em]">Leggibilità apertura</p><ContextHelpButton articleId="pdf-images" label="Guida leggibilità apertura PDF" /></div>
              <div className="grid grid-cols-2 gap-2">
                {[['template','Da template'],['off','Off'],['shadow','Ombra soft'],['dim','Dim']].map(([value,label]) => <button key={value} type="button" className="min-h-11 rounded-lg border px-2 py-3 text-xs font-semibold lg:min-h-0 lg:py-2 lg:text-[10px]" style={readability===value?{borderColor:'var(--accent-purple)',background:'rgba(168,85,247,.18)'}:{borderColor:'var(--border-color)'}} aria-pressed={readability===value} onClick={() => mutate(setReadability, value)}>{label}</button>)}
              </div>
            </section>}
            <ImageRole title="Immagine CTA" value={bookId} items={images} deletingId={deletingId} onDelete={deleteImage} onChange={value => mutate(setBookId, value)} />
          </>}

          {signatureSelected && visualMode === 'client_media' && <>
            <ImageRole title="Foto di copertina" value={coverId} items={images} deletingId={deletingId} onDelete={deleteImage} onChange={value => { mutate(setCoverId, value); if (!value) setReadability('template'); }} />
            {coverId && <section>
              <div className="mb-2 flex items-center justify-between gap-2"><p className="text-[10px] font-semibold uppercase tracking-[.12em]">Leggibilità copertina</p><ContextHelpButton articleId="pdf-images" label="Guida leggibilità copertina PDF" /></div>
              <div className="grid grid-cols-2 gap-2">
                {[['template','Da template'],['off','Off'],['shadow','Ombra soft'],['dim','Dim']].map(([value,label]) => <button key={value} type="button" className="min-h-11 rounded-lg border px-2 py-3 text-xs font-semibold lg:min-h-0 lg:py-2 lg:text-[10px]" style={readability===value?{borderColor:'var(--accent-purple)',background:'rgba(168,85,247,.18)'}:{borderColor:'var(--border-color)'}} aria-pressed={readability===value} onClick={() => mutate(setReadability, value)}>{label}</button>)}
              </div>
            </section>}
            <ImageRole title="Copertina libro · endpage" value={bookId} items={images} deletingId={deletingId} onDelete={deleteImage} onChange={value => mutate(setBookId, value)} />
          </>}

          <section className="border-t border-white/10 pt-4">
            <button type="button" className="btn-ghost flex min-h-11 w-full items-center justify-center gap-2 py-2.5 text-xs" onClick={() => { refreshPreview(); setMobilePanel('preview'); }} disabled={previewing}><ArrowClockwise size={17} />{previewStale ? 'Applica e vedi anteprima' : 'Rigenera anteprima'}</button>
          </section>
        </div>
      </aside>
    </div>

    <nav className="absolute inset-x-0 bottom-0 z-30 grid grid-cols-3 border-t border-white/10 bg-[#101017]/95 px-2 pt-2 backdrop-blur lg:hidden" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }} aria-label="Navigazione PDF Studio mobile" data-testid="master-pdf-studio-mobile-nav">
      {MOBILE_PANELS.map(([value, label]) => {
        const active = mobilePanel === value;
        return <button key={value} type="button" className={`min-h-12 rounded-xl px-2 py-2 text-xs font-semibold transition-colors ${active ? 'bg-white/10 text-white' : 'text-white/55'}`} aria-pressed={active} onClick={() => setMobilePanel(value)} data-testid={`master-pdf-studio-mobile-tab-${value}`}>{label}{value === 'preview' && previewStale && <span className="ml-1 text-amber-300">•</span>}</button>;
      })}
    </nav>
  </div>;
}
